import React, { ReactNode, FC, useState } from "react";
import Input, { InputProps } from "antd/lib/input";

import "./password-input.scss";

const regex: { [key: string]: RegExp } = {
  uppercase: /[A-Z]/g,
  lowercase: /[a-z]/g,
  number: /\d/g,
};

function getLevel(
  value: string,
  { length, symbols, ...rest }: any
): [number, any] {
  const data: any = {
    length: false,
    uppercase: false,
    lowercase: false,
    symbols: false,
    number: false,
  };

  const keys: Array<string> = Object.keys(rest);

  if (length && length > 0 && value.length >= length) {
    data.length = true;
  } else {
    data.length = false;
  }

  if (symbols && symbols.length > 0) {
    for (let i = 0; i < symbols.length; i++) {
      if (value.includes(symbols[i])) {
        data.symbols = true;
        break;
      } else {
        data.symbols = false;
      }
    }
  }

  for (let i = 0; i < keys.length; i++) {
    const key: string = keys[i];
    const matches = value.match(regex[key]);

    if (matches && matches.length >= rest[key]) {
      data[key] = true;
    } else {
      data[key] = false;
    }
  }

  const tval = Object.keys(data).filter((key: string) => data[key]);

  return [tval.length - 1, data];
}

const intialSettings: PasswordInputSettings = {
  colorScheme: {
    levels: ["#ff4033", "#fe940d", "#ffd908", "#cbe11d", "#6ecc3a"],
    noLevel: "lightgrey",
  },
  height: 3,
};

export const PasswordInput: FC<PasswordInputProps> = ({
  settings = intialSettings,
  label,
  onChange = (e: React.ChangeEvent<HTMLInputElement>, matches: any) => {},
  inputProps,
  rules,
  ...rest
}: PasswordInputProps) => {
  const [level, setLevel] = useState<number>(-1);

  function handleChange(e: any) {
    const value: string = e.target.value;
    let data = [level, {}];

    if (value) {
      data = getLevel(value, rules);
      setLevel(data[0] as number);
    } else {
      setLevel(-1);
    }

    onChange(e, data[1]);
  }

  return (
    <div className="password-input">
      <Input.Password {...inputProps} {...rest} onChange={handleChange} />
      <PasswordStrengthIndicator
        level={level}
        settings={settings!}
        label={label}
      />
    </div>
  );
};

function getIndicatorStyle(color: string, height: number) {
  return {
    display: "inline-block",
    width: "20%",
    backgroundColor: color,
    height: height + "px",
    borderRadius: "4px",
  };
}

const PasswordStrengthIndicator: FC<PasswordStrengthIndicatorProps> = ({
  level,
  settings,
  label = null,
}: PasswordStrengthIndicatorProps) => {
  const indicators: Array<ReactNode> = [];

  for (let i = 0; i < 5; i++) {
    const color =
      i <= level
        ? settings.colorScheme.levels[level]
        : settings.colorScheme.noLevel;
    indicators.push(
      <span key={i} style={getIndicatorStyle(color, settings.height)}></span>
    );
  }

  return (
    <div className="password-strength">
      {label && <label>{label}</label>}
      <div className="indicators">{indicators}</div>
    </div>
  );
};

export interface PasswordRules {
  length?: number;
  uppercase?: number;
  lowercase?: number;
  number?: number;
  symbols?: Array<string>;
}

export interface PasswordInputProps {
  settings?: PasswordInputSettings;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>, level: any) => void;
  inputProps?: InputProps;
  label?: string | ReactNode;
  rules?: PasswordRules;
}

export interface PasswordInputSettings {
  colorScheme: ColorScheme;
  height: number;
}

interface PasswordStrengthIndicatorProps {
  level: number;
  settings: PasswordInputSettings;
  label?: string | ReactNode;
}

export interface ColorScheme {
  levels: string[];
  noLevel: string;
}

export interface PasswordInputProps {}
